/* eslint-disable @typescript-eslint/no-explicit-any */

import { Bot, Inbox, MessageCircle, Search, Send, X } from 'lucide-react';
import type { FC, JSX } from 'react';
import { OmnichannelMessage } from '../../types';



interface MessagingTabProps {
  conversations: any[];
  selectedConversation: string | null;
  setSelectedConversation: (id: string | null) => void;
  searchTerm: string | undefined;
  setSearchTerm: (term: string | undefined) => void;
  type: 'sms' | 'email';
}


const MessagingTab: FC<MessagingTabProps> = ({ 
    conversations, 
    selectedConversation, 
    setSelectedConversation, 
    searchTerm, 
    setSearchTerm, 
    type 
}): JSX.Element => {

  const selectedConv = conversations.find(c => c.patientId === selectedConversation);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      {/* Conversation List */}
      <div className="lg:col-span-1 space-y-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
          <input
            type="text"
            placeholder="Search conversations..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
          />
        </div>

        <div className="space-y-2 max-h-[600px] overflow-y-auto">
          {conversations.map((conv) => (
            <button
              key={conv.patientId}
              onClick={() => setSelectedConversation(conv.patientId)}
              className={`w-full text-left p-4 rounded-lg border transition-all ${
                selectedConversation === conv.patientId
                  ? 'border-green-500 bg-green-50'
                  : 'border-gray-200 hover:border-green-300 hover:bg-gray-50'
              }`}
            >
              <div className="flex items-start justify-between mb-2">
                <h4 className="font-semibold text-gray-900">{conv.patientName}</h4>
                {conv.unreadCount > 0 && (
                  <span className="w-5 h-5 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center">
                    {conv.unreadCount}
                  </span>
                )}
              </div>
              <p className="text-sm text-gray-600 truncate">
                {conv.lastMessage.content}
              </p>
              <p className="text-xs text-gray-500 mt-1">
                {new Date(conv.lastMessage.timestamp).toLocaleString()}
              </p>
            </button>
          ))}

          {conversations.length === 0 && (
            <div className="text-center py-12">
              {type === 'sms' ? <MessageCircle className="w-12 h-12 text-gray-400 mx-auto mb-4" /> : <Inbox className="w-12 h-12 text-gray-400 mx-auto mb-4" />}
              <p className="text-gray-600">No conversations</p>
            </div>
          )}
        </div>
      </div>

      {/* Conversation Detail */}
      <div className="lg:col-span-2">
        {selectedConv ? (
          <div className="border border-gray-200 rounded-lg h-[600px] flex flex-col">
            {/* Header */}
            <div className="border-b border-gray-200 p-4 flex items-center justify-between">
              <div>
                <h3 className="font-semibold text-gray-900">{selectedConv.patientName}</h3>
                <p className="text-sm text-gray-600">{selectedConv.messages.length} messages</p>
              </div>
              <button
                onClick={() => setSelectedConversation(null)}
                className="p-2 text-gray-400 hover:text-gray-600 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {selectedConv.messages.sort((a: OmnichannelMessage, b: OmnichannelMessage) =>
                new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
              ).map((message: OmnichannelMessage) => (
                <div
                  key={message.id}
                  className={`flex ${message.direction === 'outbound' ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-[70%] rounded-lg p-4 ${
                      message.direction === 'outbound'
                        ? 'bg-green-600 text-white'
                        : 'bg-gray-100 text-gray-900'
                    }`}
                  >
                    {type === 'email' && message.subject && (
                      <p className="font-semibold mb-2">{message.subject}</p>
                    )}
                    <p className="text-sm whitespace-pre-wrap">{message.content}</p>
                    <p className={`text-xs mt-2 ${message.direction === 'outbound' ? 'text-green-100' : 'text-gray-500'}`}>
                      {new Date(message.timestamp).toLocaleString()}
                    </p>
                    {message.aiContext && (
                      <div className="mt-2 pt-2 border-t border-white border-opacity-20">
                        <p className="text-xs flex items-center">
                          <Bot className="w-3 h-3 mr-1" />
                          AI-assisted response
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Reply Input */}
            <div className="border-t border-gray-200 p-4">
              <div className="flex space-x-2">
                <input
                  type="text"
                  placeholder={`Type your ${type} reply...`}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                />
                <button className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center space-x-2">
                  <Send className="w-4 h-4" />
                  <span>Send</span>
                </button>
              </div>
            </div>
          </div>
        ) : (
          <div className="border border-gray-200 rounded-lg h-[600px] flex items-center justify-center">
            <div className="text-center">
              {type === 'sms' ? <MessageCircle className="w-16 h-16 text-gray-400 mx-auto mb-4" /> : <Inbox className="w-16 h-16 text-gray-400 mx-auto mb-4" />}
              <p className="text-gray-600">Select a conversation to view messages</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MessagingTab;